/**
 * VIB-2836: faktureringsplanen (`projects.invoice_terms`) og mellomfakturaer.
 *
 * Planen er en ordnet liste ledd `{ label, pct }` der første ledd er oppstart
 * (`invoice_type='startup'`), siste er slutt (`'final'`) og alt imellom er
 * mellomfakturaer (`'interim'`, med `milestone_index` = posisjon blant
 * mellomleddene). Gjenstående-telleren er fasit: sluttfakturaen er alltid «det
 * som gjenstår», og et planlagt ledd foreslår aldri mer enn det.
 *
 * Rene funksjoner, ingen React/Supabase — testet i __tests__/invoice-plan.test.ts.
 */
import type { DBInvoice, DBProject } from "./db-types";

export interface InvoiceTerm {
  label: string;
  pct: number;
}

/** Toleranse for prosent-sammenligning (to desimaler). */
const PCT_EPSILON = 0.01;
/** Toleranse for kronebeløp (øre-avrunding). */
const AMOUNT_EPSILON = 0.005;

/** Standard oppstartsandel når prosjektet mangler `startup_invoice_pct` (samme som fakturaberegningen). */
export const DEFAULT_STARTUP_PCT = 60;

export function roundPct(pct: number): number {
  return Math.round(pct * 100) / 100;
}

/** Prosent av kontrakten → kroner, avrundet til øre. */
export function pctToAmount(pct: number, contractAmount: number): number {
  return Math.round(contractAmount * pct) / 100;
}

/** Kroner → prosent av kontrakten, avrundet til to desimaler. 0 når kontrakten er 0. */
export function amountToPct(amount: number, contractAmount: number): number {
  if (contractAmount <= 0) return 0;
  return roundPct((amount / contractAmount) * 100);
}

export function sumTermPct(terms: InvoiceTerm[]): number {
  return roundPct(terms.reduce((acc, t) => acc + (Number(t.pct) || 0), 0));
}

/**
 * Er fakturaen en mellomfaktura? `'interim'` er den nye typen. Eldre
 * terminfakturaer ble lagret som `'upsell'` med `milestone_index` satt.
 *
 * Expand/contract: den gamle formen leses fortsatt her til backfill-migrasjonen
 * (20261007…_backfill_interim_invoice_type.sql) er verifisert i prod. Fjernes i
 * contract-steget.
 */
export function isInterimInvoice(
  inv: Pick<DBInvoice, "invoice_type" | "milestone_index">,
): boolean {
  if (inv.invoice_type === "interim") return true;
  return inv.invoice_type === "upsell" && inv.milestone_index != null;
}

/** Fakturatype for visning og filtrering, med eldre terminfakturaer tolket som mellomfaktura. */
export function effectiveInvoiceType(
  inv: Pick<DBInvoice, "invoice_type" | "milestone_index">,
): string | null {
  return isInterimInvoice(inv) ? "interim" : inv.invoice_type;
}

/**
 * Planen slik fakturaberegningen ser den. `startup_invoice_pct` er det operative
 * oppstartsfeltet (create-invoice og innboksen leser det), mens
 * `invoice_terms[0].pct` har driftet i prod. Første ledd får derfor
 * `startup_invoice_pct`, og sluttleddet tar opp differansen så summen blir 100.
 */
export function effectiveInvoiceTerms(
  project: Pick<DBProject, "invoice_terms" | "startup_invoice_pct">,
): InvoiceTerm[] {
  const raw = Array.isArray(project.invoice_terms) ? project.invoice_terms : [];
  const startupPct = project.startup_invoice_pct ?? DEFAULT_STARTUP_PCT;
  const terms: InvoiceTerm[] =
    raw.length >= 2
      ? raw.map((t) => ({ label: t.label, pct: Number(t.pct) || 0 }))
      : [
          { label: raw[0]?.label ?? "Oppstart", pct: 0 },
          { label: "Levering", pct: 0 },
        ];
  terms[0] = { ...terms[0], pct: startupPct };
  const last = terms.length - 1;
  const beforeLast = sumTermPct(terms.slice(0, last));
  terms[last] = { ...terms[last], pct: Math.max(0, roundPct(100 - beforeLast)) };
  return terms;
}

export interface TermLocks {
  /** Antall ledd fra starten som allerede er fakturert (oppstart + mellomfakturaer). */
  lockedPrefix: number;
  /** Sluttfakturaen er sendt — hele planen er da låst. */
  finalInvoiced: boolean;
}

/**
 * Hvilke ledd er allerede fakturert? Mellomfakturaer fylles fra starten av
 * planen i rekkefølge (milestone_index 0, 1, …), så de fakturerte leddene er
 * alltid et prefiks. Oppstartsleddet regnes som passert når en mellomfaktura
 * finnes, også om oppstartsandelen var 0 % og aldri ble fakturert.
 */
/** Feltene som trengs for å avgjøre om en faktura er kreditert. */
export type CreditableInvoice = Pick<DBInvoice, "id" | "status" | "amount_excl_vat" | "related_invoice_id">;

/**
 * Fakturaer som er kreditert fullt ut: summen av kreditnotaene som peker på
 * dem (`related_invoice_id`) dekker hele beløpet eks. mva. En delvis kreditert
 * faktura regnes fortsatt som sendt. Kreditnotaer laget direkte i PowerOffice
 * og synket inn har ikke `related_invoice_id`, og kan derfor ikke knyttes her.
 */
export function creditedInvoiceIds(invoices: CreditableInvoice[]): Set<string> {
  const creditedBy = new Map<string, number>();
  for (const inv of invoices) {
    if (inv.status !== "credit_note" || !inv.related_invoice_id) continue;
    const prev = creditedBy.get(inv.related_invoice_id) ?? 0;
    creditedBy.set(inv.related_invoice_id, prev + Math.abs(Number(inv.amount_excl_vat ?? 0)));
  }
  const result = new Set<string>();
  for (const inv of invoices) {
    const credited = creditedBy.get(inv.id);
    if (credited == null) continue;
    // Toleranse på 1 kr for øreavrunding mellom PO og lokalt lagret beløp.
    if (credited + 1 >= Math.abs(Number(inv.amount_excl_vat ?? 0))) result.add(inv.id);
  }
  return result;
}

/**
 * Fakturaene som teller i faktureringsplanen: kreditnotaer og fullt
 * krediterte fakturaer holdes utenfor, så en kreditert oppstartsfaktura
 * «nullstiller» leddet og kan sendes på nytt.
 */
export function planInvoices<T extends CreditableInvoice>(invoices: T[]): T[] {
  const credited = creditedInvoiceIds(invoices);
  return invoices.filter((i) => i.status !== "credit_note" && !credited.has(i.id));
}

export function computeTermLocks(
  invoices: (Pick<DBInvoice, "invoice_type" | "milestone_index"> & Partial<CreditableInvoice>)[],
): TermLocks {
  const active = planInvoices(
    invoices.map((i) => ({
      ...i,
      id: i.id ?? "",
      status: i.status ?? null,
      amount_excl_vat: i.amount_excl_vat ?? null,
      related_invoice_id: i.related_invoice_id ?? null,
    })),
  );
  const interimCount = active.filter((i) => i.milestone_index != null).length;
  const hasStartup = active.some((i) => i.invoice_type === "startup");
  const startupLocked = hasStartup || interimCount > 0 ? 1 : 0;
  return {
    lockedPrefix: startupLocked + interimCount,
    finalInvoiced: active.some((i) => i.invoice_type === "final"),
  };
}

/** Er ledd nr. `index` låst i en plan med `termCount` ledd? */
export function isTermLocked(locks: TermLocks, index: number, termCount: number): boolean {
  if (locks.finalInvoiced) return true;
  // Sluttleddet er aldri en del av prefikset — det tar opp differansen til det er fakturert.
  return index < Math.min(locks.lockedPrefix, termCount - 1);
}

/** Summen (i %) av de låste leddene, dvs. det som allerede er fakturert ifølge planen. */
export function lockedPct(terms: InvoiceTerm[], locks: TermLocks): number {
  return sumTermPct(terms.filter((_, i) => isTermLocked(locks, i, terms.length)));
}

/**
 * Validerer en endret plan mot den gjeldende. Returnerer en norsk feilmelding,
 * eller null når planen kan lagres.
 */
export function validateInvoicePlanEdit(
  current: InvoiceTerm[],
  next: InvoiceTerm[],
  locks: TermLocks,
): string | null {
  if (locks.finalInvoiced) {
    return "Sluttfakturaen er sendt. Planen kan ikke endres.";
  }
  const lockedCount = Math.min(locks.lockedPrefix, current.length - 1);
  if (next.length < lockedCount + 1) {
    return "Planen må ha et sluttledd etter de fakturerte leddene.";
  }
  for (let i = 0; i < lockedCount; i++) {
    if (next[i].label !== current[i].label || roundPct(next[i].pct) !== roundPct(current[i].pct)) {
      return "Ledd som allerede er fakturert kan ikke endres eller fjernes.";
    }
  }
  if (next.length < 2) {
    return "Planen må ha minst oppstart og slutt.";
  }
  if (next.some((t) => !t.label.trim())) {
    return "Alle ledd må ha et navn.";
  }
  if (next.some((t) => !(Number.isFinite(t.pct) && t.pct >= 0))) {
    return "Prosentene kan ikke være negative.";
  }
  const remainingPct = roundPct(100 - lockedPct(current, locks));
  const openPct = sumTermPct(next.slice(lockedCount));
  if (Math.abs(openPct - remainingPct) >= PCT_EPSILON) {
    return `Leddene som gjenstår må til sammen være ${remainingPct} % (er ${openPct} %).`;
  }
  return null;
}

/**
 * Legger en ad hoc-mellomfaktura inn i planen som et fakturert ledd på
 * `position` (rett etter de allerede fakturerte leddene). Sluttleddet reduseres
 * tilsvarende. Er sluttleddet for lite, kappes de planlagte mellomleddene etter
 * det nye leddet bakfra, og et planlagt mellomledd som havner på 0 % fjernes
 * (det ville foreslått en faktura på 0 kr).
 */
export function insertAdHocInterim(
  terms: InvoiceTerm[],
  position: number,
  pct: number,
  label = "Mellomfaktura",
): InvoiceTerm[] {
  if (terms.length < 2) throw new Error("Planen må ha minst oppstart og slutt.");
  if (position < 1 || position > terms.length - 1) {
    throw new Error(`Ugyldig plassering ${position} for mellomfaktura.`);
  }
  const next = terms.map((t) => ({ ...t }));
  next.splice(position, 0, { label, pct: roundPct(pct) });

  let overflow = roundPct(pct);
  // Sluttleddet først, så planlagte mellomledd bakfra (aldri det nye leddet eller tidligere ledd).
  for (let i = next.length - 1; i > position && overflow > 0; i--) {
    const take = Math.min(next[i].pct, overflow);
    next[i] = { ...next[i], pct: roundPct(next[i].pct - take) };
    overflow = roundPct(overflow - take);
  }
  if (overflow > 0) {
    throw new Error("Mellomfakturaen er større enn det som gjenstår av planen.");
  }
  const last = next.length - 1;
  return next.filter((t, i) => i <= position || i === last || t.pct > 0);
}

/**
 * Validerer beløpet på en ad hoc-mellomfaktura. `remaining` er det som gjenstår
 * av kontrakten (eks. ufakturert mersalg, som faktureres på sluttfakturaen).
 */
export function validateInterimAmount(amount: number, remaining: number): string | null {
  if (!Number.isFinite(amount) || amount <= 0) {
    return "Beløpet må være større enn 0.";
  }
  if (amount > remaining + AMOUNT_EPSILON) {
    return "Beløpet kan ikke være mer enn det som gjenstår av kontrakten.";
  }
  return null;
}
